export const SLOTS_PER_HOUR = 4;
/** Visible calendar day window (inclusive start, exclusive end). */
export const START_HOUR = 5;
export const END_HOUR = 20;
export const TOTAL_SLOTS = (END_HOUR - START_HOUR) * SLOTS_PER_HOUR;
/** Default work-block start (wall-clock hour) within the visible window. */
export const DEFAULT_WORK_START_HOUR = 8;
/** Max selectable work hours (= visible window length). */
export const MAX_PLAN_HOURS = END_HOUR - START_HOUR;
export const DAY_LABELS = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

/** Convert a wall-clock hour (e.g. 8 for 08:00) to a grid slot. */
export function wallHourToSlot(hour) {
  const h = Number(hour);
  if (!Number.isFinite(h)) return 0;
  return clamp(
    Math.round((h - START_HOUR) * SLOTS_PER_HOUR),
    0,
    TOTAL_SLOTS - 1
  );
}

const MONTHS = [
  "januar",
  "februar",
  "mars",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "desember",
];
const MONTHS_SHORT = [
  "jan",
  "feb",
  "mar",
  "apr",
  "mai",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "des",
];

const AVATAR_COLORS = [
  "#1f8ce8",
  "#7c3aed",
  "#f59e0b",
  "#ec4899",
  "#0891b2",
  "#16a34a",
  "#dc2626",
  "#8b5cf6",
];

export function startOfWeek(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function fmtNo(d, locale = "nb-NO") {
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${d.getDate()}. ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function fmtNoShort(d) {
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${DAY_LABELS[(d.getDay() + 6) % 7]} ${d.getDate()}. ${MONTHS_SHORT[d.getMonth()]}`;
}

export function slotToTime(slot) {
  if (!Number.isFinite(Number(slot))) return "";
  const s = clamp(Math.floor(Number(slot)), 0, TOTAL_SLOTS);
  const total = START_HOUR * 60 + s * 15;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function timeToSlot(date) {
  const h = date.getHours();
  const m = date.getMinutes();
  const mins = h * 60 + m;
  const startMins = START_HOUR * 60;
  return clamp(Math.floor((mins - startMins) / 15), 0, TOTAL_SLOTS - 1);
}

const SLOT_MS = 15 * 60 * 1000;

/** Build local Date for calendar day + HH:mm (avoids UTC drift on save). */
export function localDateTimeFromParts(dateStr, timeStr) {
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi] = timeStr.split(":").map(Number);
  return new Date(y, mo - 1, d, h, mi, 0, 0);
}

export function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

export function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function avatarColorForId(id) {
  const n = Number(id) || String(id).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[Math.abs(n) % AVATAR_COLORS.length];
}

export function formatWeekLabel(weekStart, weekEnd) {
  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${weekStart.getDate()}. – ${weekEnd.getDate()}. ${MONTHS[weekStart.getMonth()]} ${weekStart.getFullYear()}`;
  }
  return `${weekStart.getDate()}. ${MONTHS_SHORT[weekStart.getMonth()]} – ${weekEnd.getDate()}. ${MONTHS_SHORT[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`;
}

export function toDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const PLAN_DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** Plan selection day key (yyyy-MM-dd), not week index 0–6. */
export function isPlanDateKey(day) {
  return typeof day === "string" && PLAN_DATE_KEY.test(day);
}

export function sortPlanDayKeys(a, b) {
  if (isPlanDateKey(a) && isPlanDateKey(b)) return a.localeCompare(b);
  return Number(a) - Number(b);
}

export function planDayKey(weekStart, dayIndex) {
  return toDateString(addDays(weekStart, dayIndex));
}

/** Week column index for a plan day key, or null if outside this week. */
export function planDayIndex(weekStart, dayKey) {
  if (!weekStart || dayKey == null) return null;
  if (!isPlanDateKey(dayKey)) {
    const n = Number(dayKey);
    return Number.isFinite(n) && n >= 0 && n <= 6 ? n : null;
  }
  const ws = new Date(weekStart);
  ws.setHours(0, 0, 0, 0);
  const d = new Date(`${dayKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  const idx = Math.round((d.getTime() - ws.getTime()) / 86400000);
  return idx >= 0 && idx <= 6 ? idx : null;
}

export function planDayInWeek(weekStart, dayKey) {
  return planDayIndex(weekStart, dayKey) != null;
}

/** Calendar date for a plan day key (date string or legacy 0–6 + anchor week). */
export function planDayToDate(dayKey, anchorWeekStart = null) {
  if (dayKey == null) return null;
  if (isPlanDateKey(dayKey)) {
    const d = new Date(`${dayKey}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const n = Number(dayKey);
  if (!Number.isFinite(n) || n < 0 || n > 6 || !anchorWeekStart) return null;
  return addDays(anchorWeekStart, n);
}

export function planDayWeekdayLabel(dayKey, anchorWeekStart = null) {
  const d = planDayToDate(dayKey, anchorWeekStart);
  if (!d) return "";
  const idx = (d.getDay() + 6) % 7;
  return DAY_LABELS[idx] ?? "";
}

/**
 * Normalize plan days to yyyy-MM-dd, merge duplicate keys (e.g. 0 + same date),
 * and drop invalid entries. anchorWeekStart resolves legacy column indices only.
 */
export function canonicalizePlanSelection(selection, anchorWeekStart = null) {
  if (!selection) return selection;
  const merged = new Map();

  const absorb = (rawKey) => {
    let dateKey = null;
    if (isPlanDateKey(rawKey)) {
      dateKey = rawKey;
    } else {
      const n = Number(rawKey);
      if (Number.isFinite(n) && n >= 0 && n <= 6 && anchorWeekStart) {
        dateKey = planDayKey(anchorWeekStart, n);
      }
    }
    if (!dateKey) return;
    const slots = getDaySchedule(selection, rawKey);
    if (slots?.startSlot == null || slots?.endSlot == null) return;
    const prev = merged.get(dateKey);
    if (!prev) {
      merged.set(dateKey, { startSlot: slots.startSlot, endSlot: slots.endSlot });
      return;
    }
    merged.set(dateKey, {
      startSlot: Math.min(prev.startSlot, slots.startSlot),
      endSlot: Math.max(prev.endSlot, slots.endSlot),
    });
  };

  (selection.days || []).forEach(absorb);
  Object.keys(selection.daySchedules || {}).forEach(absorb);

  const days = [...merged.keys()].sort(sortPlanDayKeys);
  const daySchedules = Object.fromEntries(merged);
  const firstSlots = days[0] != null ? daySchedules[days[0]] : null;
  return {
    ...selection,
    days,
    daySchedules,
    ...(firstSlots
      ? { startSlot: firstSlots.startSlot, endSlot: firstSlots.endSlot }
      : {}),
  };
}

/** Inclusive calendar dates between two plan day keys. */
export function planDateRangeKeys(startKey, endKey) {
  if (!isPlanDateKey(startKey) || !isPlanDateKey(endKey)) return [];
  const out = [];
  const cur = new Date(`${startKey}T12:00:00`);
  const end = new Date(`${endKey}T12:00:00`);
  if (Number.isNaN(cur.getTime()) || Number.isNaN(end.getTime())) return [];
  cur.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (cur > end) return [startKey];
  while (cur <= end) {
    out.push(toDateString(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export function formatAppointmentTime(appointment, fixedTime) {
  if (!appointment) return "—";
  const start = slotToTime(appointment.startSlot);
  const end = slotToTime(appointment.endSlot);
  if (fixedTime) return start;
  return `${start} – ${end}`;
}

export function formatAppointmentTimeDetail(appointment, fixedTime) {
  if (!appointment) return "—";
  const start = slotToTime(appointment.startSlot);
  const end = slotToTime(appointment.endSlot);
  if (fixedTime) return `Tidspunkt: ${start}`;
  return `Tidsrom: ${start} til ${end}`;
}

export function appointmentToFormValues(appointment, weekStart, fixedTime) {
  if (!appointment) return null;
  const date = addDays(weekStart, appointment.day);
  const start = slotToTime(appointment.startSlot);
  const end = slotToTime(appointment.endSlot);
  return {
    agreement_date: toDateString(date),
    agreement_time: fixedTime ? start : null,
    agreement_time_from: fixedTime ? null : start,
    agreement_time_too: fixedTime ? null : end,
  };
}

export function formValuesToAppointment(values, weekStart) {
  if (!values?.agreement_date) return null;
  const date = new Date(`${values.agreement_date}T12:00:00`);
  const day = Math.round(
    (date.setHours(0, 0, 0, 0) - weekStart.setHours(0, 0, 0, 0)) / 86400000
  );
  if (day < 0 || day > 6) return null;

  if (values.agreement_time) {
    const [h, m] = values.agreement_time.split(":").map(Number);
    const startSlot = clamp(
      Math.floor(((h * 60 + m) - START_HOUR * 60) / 15),
      0,
      TOTAL_SLOTS - 2
    );
    return { day, startSlot, endSlot: startSlot + 2 };
  }
  if (values.agreement_time_from && values.agreement_time_too) {
    const [fh, fm] = values.agreement_time_from.split(":").map(Number);
    const [th, tm] = values.agreement_time_too.split(":").map(Number);
    const startSlot = clamp(
      Math.floor(((fh * 60 + fm) - START_HOUR * 60) / 15),
      0,
      TOTAL_SLOTS - 1
    );
    const endSlot = clamp(
      Math.ceil(((th * 60 + tm) - START_HOUR * 60) / 15),
      startSlot + 1,
      TOTAL_SLOTS
    );
    return { day, startSlot, endSlot };
  }
  return null;
}

export function parseCalendarDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const datePart = String(value).split("T")[0];
  const [y, mo, d] = datePart.split("-").map(Number);
  if (!y || !mo || !d) return null;
  const parsed = new Date(y, mo - 1, d);
  parsed.setHours(0, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function dayIndexInWeek(date, weekStart) {
  const dayStart = new Date(weekStart);
  dayStart.setHours(0, 0, 0, 0);
  const d = date instanceof Date ? new Date(date) : parseCalendarDate(date);
  if (!d) return -1;
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - dayStart.getTime()) / 86400000);
}

export function weekRangeParams(weekStart) {
  return {
    date_from: toDateString(weekStart),
    date_too: toDateString(addDays(weekStart, 6)),
  };
}

export function dayIndicesForDateRange(fromDate, toDate, weekStart) {
  const from = parseCalendarDate(fromDate);
  const to = parseCalendarDate(toDate || fromDate);
  if (!from || !to) return [];

  const weekEnd = addDays(weekStart, 6);
  let cur = new Date(Math.max(from.getTime(), weekStart.getTime()));
  cur.setHours(0, 0, 0, 0);
  const end = new Date(Math.min(to.getTime(), weekEnd.getTime()));
  end.setHours(0, 0, 0, 0);

  const indices = [];
  while (cur <= end) {
    const idx = dayIndexInWeek(cur, weekStart);
    if (idx >= 0 && idx <= 6) indices.push(idx);
    cur = addDays(cur, 1);
  }
  return indices;
}

export function isActiveAbsenceRecord(absence) {
  return Boolean(absence && !absence.denied);
}

export function absenceRecordToBlocks(absence, weekStart, fallbackTitle = "Fravær") {
  if (!isActiveAbsenceRecord(absence)) return [];

  const title =
    absence.displayTitle ||
    absence.absence_type?.displayTitle ||
    absence.absence_type?.name ||
    fallbackTitle;

  return dayIndicesForDateRange(absence.date_from, absence.date_too, weekStart).map(
    (day) => ({
      id: `absence-${absence.id}-${day}`,
      day,
      startSlot: 0,
      endSlot: TOTAL_SLOTS,
      title,
      kind: "absence",
      allDay: true,
    })
  );
}

export function calendarEventToBlock(ev, weekStart) {
  const start = ev?.start_time ? new Date(ev.start_time) : null;
  const end = ev?.end_time ? new Date(ev.end_time) : null;
  if (!start || !end || Number.isNaN(start.getTime())) return null;

  const dayStart = new Date(weekStart);
  dayStart.setHours(0, 0, 0, 0);
  const startDay = new Date(start);
  startDay.setHours(0, 0, 0, 0);
  const dayIdx = Math.round((startDay.getTime() - dayStart.getTime()) / 86400000);
  if (dayIdx < 0 || dayIdx > 6) return null;

  const startSlot = timeToSlot(start);
  let endSlot = timeToSlot(end);
  if (endSlot <= startSlot) endSlot = Math.min(startSlot + 2, TOTAL_SLOTS);

  const isAbsence = ev?.module === "absence";
  const title = isAbsence
    ? ev?.module_details?.absence_type ||
      ev?.module_details?.displayTitle ||
      ev?.title ||
      "Fravær"
    : ev?.module_details?.displayTitle ||
      ev?.title ||
      ev?.module_details?.report_type ||
      "";
  const customer =
    ev?.module_details?.customer_name ||
    ev?.module_details?.description ||
    ev?.location ||
    "";

  return {
    id: ev.id || `${dayIdx}-${startSlot}`,
    day: dayIdx,
    startSlot,
    endSlot,
    title,
    customer,
    kind: isAbsence ? "absence" : "busy",
    allDay: Boolean(ev?.all_day),
  };
}

export function eventToCalendarBlocks(ev, weekStart, absenceLabel = "Fravær") {
  const isAbsence = ev?.module === "absence";

  if (isAbsence || ev?.all_day) {
    let from = ev?.start_time ? parseCalendarDate(ev.start_time) : null;
    let to = ev?.end_time ? parseCalendarDate(ev.end_time) : from;

    if (!from && ev?.module_details) {
      from = parseCalendarDate(
        ev.module_details.start_date || ev.module_details.date_from
      );
      to = parseCalendarDate(
        ev.module_details.end_date || ev.module_details.date_too || from
      );
    }

    if (!from) return [];

    const title = isAbsence
      ? ev?.module_details?.absence_type ||
        ev?.module_details?.displayTitle ||
        ev?.title ||
        absenceLabel
      : ev?.module_details?.displayTitle || ev?.title || "";

    return dayIndicesForDateRange(from, to, weekStart).map((day) => ({
      id: `${ev?.id || "ev"}-${day}`,
      day,
      startSlot: 0,
      endSlot: TOTAL_SLOTS,
      title,
      kind: isAbsence ? "absence" : "busy",
      allDay: true,
    }));
  }

  const block = calendarEventToBlock(ev, weekStart);
  return block ? [block] : [];
}

export function buildWeekScheduleBlocks(events, absences, weekStart, absenceLabel = "Fravær") {
  const blocks = (events || []).flatMap((ev) =>
    eventToCalendarBlocks(ev, weekStart, absenceLabel)
  );

  for (const absence of absences || []) {
    for (const block of absenceRecordToBlocks(absence, weekStart, absenceLabel)) {
      if (!blocks.some((b) => b.kind === "absence" && b.day === block.day)) {
        blocks.push(block);
      }
    }
  }

  return blocks;
}

export function absenceDaySet(absences, weekStart) {
  const set = new Set();
  for (const absence of absences || []) {
    if (!isActiveAbsenceRecord(absence)) continue;
    for (const day of dayIndicesForDateRange(
      absence.date_from,
      absence.date_too,
      weekStart
    )) {
      set.add(day);
    }
  }
  return set;
}

export function availabilityFromEventCount(count) {
  if (count <= 4) return "free";
  if (count <= 7) return "part";
  return "busy";
}

export function availabilityWithAbsence({ eventCount, absenceDays, focusDay }) {
  if (absenceDays?.size > 0) {
    if (focusDay != null && absenceDays.has(focusDay)) return "absence";
    return "absence";
  }
  return availabilityFromEventCount(eventCount);
}

export function hasOverlap(a, b) {
  return !(a.endSlot <= b.startSlot || a.startSlot >= b.endSlot);
}

/** Map resource to calendar date + slot range (all weeks). */
export function resourceToDaySlots(resource) {
  if (!resource?.start_time || !resource?.end_time) return null;
  const start = new Date(resource.start_time);
  const end = new Date(resource.end_time);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

  const startSlot = timeToSlot(start);
  const endMs = end.getTime() - start.getTime();
  if (endMs <= 0) return null;

  const slotSpan = Math.max(1, Math.round(endMs / SLOT_MS));
  const endSlot = Math.min(startSlot + slotSpan, TOTAL_SLOTS);

  return { date: toDateString(start), startSlot, endSlot };
}

/** Map resource to week calendar column (0–6) when it falls in weekStart's week. */
export function resourceToAppointment(resource, weekStart) {
  const slots = resourceToDaySlots(resource);
  if (!slots || !weekStart) return null;
  const day = planDayIndex(weekStart, slots.date);
  if (day == null) return null;
  return { day, startSlot: slots.startSlot, endSlot: slots.endSlot };
}

function normalizeDaySlots(appointment) {
  if (!appointment) return null;
  const startSlot = clamp(
    Math.floor(Number(appointment.startSlot)),
    0,
    TOTAL_SLOTS - 1
  );
  let endSlot = clamp(
    Math.ceil(Number(appointment.endSlot)),
    startSlot + 1,
    TOTAL_SLOTS
  );
  if (!Number.isFinite(startSlot) || !Number.isFinite(endSlot)) return null;
  if (endSlot <= startSlot) return null;
  return { startSlot, endSlot };
}

export function appointmentToResourceTimesByDate(dateStr, startSlot, endSlot) {
  if (!dateStr || !isPlanDateKey(dateStr)) return null;
  const normalized = normalizeDaySlots({ startSlot, endSlot });
  if (!normalized) return null;

  const startTimeStr = slotToTime(normalized.startSlot);
  if (!startTimeStr) return null;

  const spanMs = (normalized.endSlot - normalized.startSlot) * SLOT_MS;
  if (spanMs <= 0) return null;

  const startDate = localDateTimeFromParts(dateStr, startTimeStr);
  if (Number.isNaN(startDate.getTime())) return null;

  const endDate = new Date(startDate.getTime() + spanMs);
  if (Number.isNaN(endDate.getTime())) return null;

  return {
    start_date: dateStr,
    start_time: startDate.toISOString(),
    end_time: endDate.toISOString(),
  };
}

export function appointmentToResourceTimes(appointment, weekStart) {
  if (!appointment) return null;
  const normalized = normalizeDaySlots(appointment);
  if (!normalized) return null;

  if (isPlanDateKey(appointment.day)) {
    return appointmentToResourceTimesByDate(
      appointment.day,
      normalized.startSlot,
      normalized.endSlot
    );
  }

  if (!weekStart) return null;
  const ws = weekStart instanceof Date ? weekStart : new Date(weekStart);
  if (Number.isNaN(ws.getTime())) return null;

  const day = Number(appointment.day);
  if (!Number.isFinite(day) || day < 0 || day > 6) return null;

  return appointmentToResourceTimesByDate(
    toDateString(addDays(ws, day)),
    normalized.startSlot,
    normalized.endSlot
  );
}

export function hoursToDurationSlots(hours) {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return 4;
  return clamp(Math.round(h * SLOTS_PER_HOUR), 1, TOTAL_SLOTS);
}

export function slotsToHours(startSlot, endSlot) {
  const mins = (endSlot - startSlot) * 15;
  return Math.round((mins / 60) * 100) / 100;
}

export function parseTimeToSlot(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return null;
  const m = timeStr.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  const mins = h * 60 + min;
  const startMins = START_HOUR * 60;
  const endMins = END_HOUR * 60;
  if (mins < startMins || mins >= endMins) return null;
  return clamp(Math.floor((mins - startMins) / 15), 0, TOTAL_SLOTS - 1);
}

/** Slots for one day (per-day map or shared fallback). */
export function getDaySchedule(selection, dayIndex) {
  if (!selection) return null;
  const keyed = selection.daySchedules?.[dayIndex];
  if (keyed?.startSlot != null && keyed?.endSlot != null) return keyed;
  if (
    selection.startSlot != null &&
    selection.endSlot != null &&
    (selection.days || []).includes(dayIndex)
  ) {
    return { startSlot: selection.startSlot, endSlot: selection.endSlot };
  }
  return null;
}

export function defaultDaySlots(hours = 8) {
  const startSlot = wallHourToSlot(DEFAULT_WORK_START_HOUR);
  const span = hoursToDurationSlots(hours);
  return { startSlot, endSlot: Math.min(startSlot + span, TOTAL_SLOTS) };
}

export function mergeDaySchedules(selection, dayIndices, slots) {
  const daySchedules = { ...(selection?.daySchedules || {}) };
  (dayIndices || []).forEach((day) => {
    daySchedules[day] = {
      startSlot: slots.startSlot,
      endSlot: slots.endSlot,
    };
  });
  return daySchedules;
}

export function applyHoursToDays(selection, hours, dayIndices = null) {
  const days = dayIndices ?? selection?.days ?? [];
  const span = hoursToDurationSlots(hours);
  const daySchedules = { ...(selection?.daySchedules || {}) };
  days.forEach((day) => {
    const existing = getDaySchedule(selection, day);
    const startSlot =
      existing?.startSlot ??
      selection?.startSlot ??
      wallHourToSlot(DEFAULT_WORK_START_HOUR);
    daySchedules[day] = {
      startSlot,
      endSlot: Math.min(startSlot + span, TOTAL_SLOTS),
    };
  });
  const firstDay = days[0];
  const firstSlots = firstDay != null ? daySchedules[firstDay] : null;
  return {
    ...selection,
    daySchedules,
    ...(firstSlots
      ? { startSlot: firstSlots.startSlot, endSlot: firstSlots.endSlot }
      : {}),
  };
}

export function planSelectionHasValidSchedule(selection) {
  const norm = normalizePlanSelection(selection);
  if (!norm.days.length) return false;
  return norm.days.every((day) => {
    const s = getDaySchedule(selection, day);
    return (
      s?.startSlot != null &&
      s?.endSlot != null &&
      s.endSlot > s.startSlot &&
      s.endSlot <= TOTAL_SLOTS
    );
  });
}

/** Plan schedule: days in week (0–6) + shared or per-day time slots. */
export function normalizePlanSelection(sel) {
  if (!sel) return { days: [], startSlot: 0, endSlot: 2, daySchedules: {} };
  if (Array.isArray(sel.days) && sel.days.length) {
    return {
      days: [...new Set(sel.days)].sort(sortPlanDayKeys),
      startSlot: sel.startSlot ?? 0,
      endSlot: sel.endSlot ?? 2,
      daySchedules: sel.daySchedules || {},
    };
  }
  if (sel.startDay != null && sel.endDay != null) {
    const days = [];
    for (let d = sel.startDay; d <= sel.endDay; d += 1) days.push(d);
    return {
      days,
      startSlot: sel.startSlot ?? 0,
      endSlot: sel.endSlot ?? 2,
      daySchedules: sel.daySchedules || {},
    };
  }
  if (sel.day != null) {
    return {
      days: [sel.day],
      startSlot: sel.startSlot ?? 0,
      endSlot: sel.endSlot ?? 2,
      daySchedules: sel.daySchedules || {},
    };
  }
  return { days: [], startSlot: 0, endSlot: 2, daySchedules: {} };
}

export function planSelectionToDaySpan(sel) {
  const norm = normalizePlanSelection(sel);
  if (!norm.days.length) return null;
  return {
    startDay: norm.days[0],
    endDay: norm.days[norm.days.length - 1],
    startSlot: norm.startSlot,
    endSlot: norm.endSlot,
    days: norm.days,
  };
}

export function buildPlanResourcesPayload(selection, weekStart, employeeIds, existingResources = []) {
  const norm = normalizePlanSelection(selection);
  if (!norm.days.length || !employeeIds?.length) return [];

  const existingMap = new Map();
  (existingResources || []).forEach((r) => {
    if (!r?.user_id || !r?.start_time) return;
    const dateStr = toDateString(new Date(r.start_time));
    existingMap.set(`${r.user_id}-${dateStr}`, r);
  });

  const resources = [];
  employeeIds.forEach((userId) => {
    norm.days.forEach((dayKey) => {
      const daySlots = getDaySchedule(selection, dayKey);
      if (!daySlots) return;
      const dateStr = isPlanDateKey(dayKey)
        ? dayKey
        : weekStart
          ? toDateString(addDays(weekStart, dayKey))
          : null;
      if (!dateStr) return;
      const times = appointmentToResourceTimesByDate(
        dateStr,
        daySlots.startSlot,
        daySlots.endSlot
      );
      if (!times) return;
      const prev = existingMap.get(`${userId}-${dateStr}`);
      resources.push({
        id: prev?.id || null,
        user_id: Number(userId),
        start_time: times.start_time,
        end_time: times.end_time,
      });
    });
  });
  return resources;
}

function selectionSignature(selection) {
  const days = [...(selection?.days || [])].sort(sortPlanDayKeys);
  return days
    .map((dayKey) => {
      const s = getDaySchedule(selection, dayKey);
      if (!s) return String(dayKey);
      return `${dayKey}:${s.startSlot}-${s.endSlot}`;
    })
    .join("|");
}

function selectionFromResourceList(resources, fallbackSlots) {
  const days = new Set();
  const daySchedules = {};
  let startSlot = null;
  let endSlot = null;

  (resources || []).forEach((r) => {
    const apt = resourceToDaySlots(r);
    if (!apt) return;
    days.add(apt.date);
    const prev = daySchedules[apt.date];
    if (!prev) {
      daySchedules[apt.date] = {
        startSlot: apt.startSlot,
        endSlot: apt.endSlot,
      };
    } else {
      daySchedules[apt.date] = {
        startSlot: Math.min(prev.startSlot, apt.startSlot),
        endSlot: Math.max(prev.endSlot, apt.endSlot),
      };
    }
    if (startSlot == null || apt.startSlot < startSlot) startSlot = apt.startSlot;
    if (endSlot == null || apt.endSlot > endSlot) endSlot = apt.endSlot;
  });

  const sortedDays = [...days].sort(sortPlanDayKeys);
  const firstDay = sortedDays[0];
  const firstSlots = firstDay != null ? daySchedules[firstDay] : null;
  const anchorDate = firstDay
    ? new Date(`${firstDay}T12:00:00`)
    : resources?.[0]?.start_time
      ? new Date(resources[0].start_time)
      : new Date();
  const weekStart = startOfWeek(anchorDate);
  const selection = canonicalizePlanSelection(
    {
      days: sortedDays,
      startSlot:
        firstSlots?.startSlot ??
        startSlot ??
        fallbackSlots.startSlot,
      endSlot:
        firstSlots?.endSlot ?? endSlot ?? fallbackSlots.endSlot,
      daySchedules,
    },
    weekStart
  );
  let estimatedHours = 8;
  if (firstSlots) {
    estimatedHours = slotsToHours(firstSlots.startSlot, firstSlots.endSlot);
  }
  return { selection, estimatedHours, weekStart, firstDay };
}

/**
 * Hydrate modal schedule state from project_event_resources.
 * When resources share identical day+time sets → planMode "shared".
 * When day sets diverge → planMode "per_user" with empty shared template
 * so "Samme plan for alle" is not auto-selected / applied.
 */
export function hydratePlanSelectionFromResources(resources) {
  const list = resources || [];
  const slots = defaultDaySlots(8);
  if (!list.length) {
    return {
      employeeIds: [],
      selection: { days: [], ...slots, daySchedules: {} },
      selectionByUser: {},
      planMode: "shared",
      estimatedHours: 8,
      weekStart: startOfWeek(new Date()),
    };
  }

  const byUser = new Map();
  list.forEach((r) => {
    if (!r?.user_id) return;
    if (!byUser.has(r.user_id)) byUser.set(r.user_id, []);
    byUser.get(r.user_id).push(r);
  });
  const employeeIds = [...byUser.keys()];

  const selectionByUser = {};
  const signatures = [];
  let weekStart = startOfWeek(new Date());
  let estimatedHours = 8;
  let earliestDay = null;

  employeeIds.forEach((uid) => {
    const built = selectionFromResourceList(byUser.get(uid), slots);
    selectionByUser[uid] = built.selection;
    signatures.push(selectionSignature(built.selection));
    if (built.firstDay && (!earliestDay || built.firstDay < earliestDay)) {
      earliestDay = built.firstDay;
      weekStart = built.weekStart;
      estimatedHours = built.estimatedHours;
    }
  });

  const allSame =
    signatures.length > 0 && signatures.every((s) => s === signatures[0]);

  if (allSame) {
    const shared = selectionByUser[employeeIds[0]] || {
      days: [],
      ...slots,
      daySchedules: {},
    };
    return {
      employeeIds,
      selection: shared,
      selectionByUser: {},
      planMode: "shared",
      estimatedHours,
      weekStart,
    };
  }

  // Divergent day sets: keep per-user overrides; shared template stays empty
  // so saving in "same for all" cannot silently expand historical days.
  return {
    employeeIds,
    selection: { days: [], ...slots, daySchedules: {} },
    selectionByUser,
    planMode: "per_user",
    estimatedHours,
    weekStart,
  };
}

export function planSelectionDateRange(selection, weekStart = null) {
  const norm = normalizePlanSelection(selection);
  if (!norm.days.length) return { start_date: null, end_date: null };
  const dateKeys = norm.days
    .map((dayKey) =>
      isPlanDateKey(dayKey)
        ? dayKey
        : weekStart
          ? toDateString(addDays(weekStart, dayKey))
          : null
    )
    .filter(Boolean)
    .sort();
  if (!dateKeys.length) return { start_date: null, end_date: null };
  return { start_date: dateKeys[0], end_date: dateKeys[dateKeys.length - 1] };
}
